import BaseParser from './BaseParser';
import _get from 'lodash/get';

import { filterExtensions } from './util';
import Address from './Address';
import Annotation from './Annotation';
import CodeableConcept from './CodeableConcept';
import Coding from './Coding';
import FhirDateTime from './FhirDateTime';
import FhirIdentifier from './Identifier';
import Period from './Period';
import Primitive from './Primitive';
import Quantity from './Quantity';
import Range from './Range';
import Ratio from './Ratio';
import HumanName from './HumanName';
import ContactPoint from './ContactPoint';
import {
  BaseParserOptions,
  ExtensionOptions,
  BaseParserConstructor,
} from './SharedTypes';

const primitiveRelativePaths = [
  'valueBoolean',
  'valueInteger',
  'valueString',
  'valueDecimal',
  'valueUri',
  'valueBase64Binary',
  'valueCode',
  'valueOid',
  'valueId',
  'valueMarkdown',
  'valueUnsignedInt',
  'valuePositiveInt',
];

export type ExtensionTypeMapping = {
  relativePath: string;
  Parser: BaseParserConstructor;
};

const extensionTypeMappings: ExtensionTypeMapping[] = [
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  { relativePath: 'valueAddress', Parser: Address },
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  { relativePath: 'valueCodeableConcept', Parser: CodeableConcept },
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  { relativePath: 'valueQuantity', Parser: Quantity },
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  { relativePath: 'valueCoding', Parser: Coding },
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  { relativePath: 'valuePeriod', Parser: Period },
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  { relativePath: 'valueIdentifier', Parser: FhirIdentifier },
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  { relativePath: 'valueDateTime', Parser: FhirDateTime },
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  { relativePath: 'valueInstant', Parser: FhirDateTime },
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  { relativePath: 'valueDate', Parser: FhirDateTime },
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  { relativePath: 'valueAnnotation', Parser: Annotation },
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  { relativePath: 'valueRange', Parser: Range },
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  { relativePath: 'valueRatio', Parser: Ratio },
  { relativePath: 'valueHumanName', Parser: HumanName },
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  { relativePath: 'valueContactPoint', Parser: ContactPoint },
  ...primitiveRelativePaths.map(relativePath => ({
    relativePath,
    Parser: Primitive,
  })),
];

class Extension extends BaseParser {
  private options: BaseParserOptions;

  /**
   * @param {string|string[]} options.extension.url - the target extension url
   * @param {Object} options - extra options passed to extension value parser
   */
  constructor(options: BaseParserOptions) {
    super();
    this.options = options;
  }

  getTargetExtension(fhirProperty: fhir.Extension[]) {
    const { url } = _get<BaseParserOptions, 'extension', ExtensionOptions>(
      this.options,
      'extension',
      {},
    );

    if (!url) throw new Error('Extension parser must specify atleast 1 url');

    const extensions = fhirProperty || [];
    if (!extensions || extensions.length === 0) return null;

    return filterExtensions(extensions, url);
  }

  getExtensionTypeMapping(
    targetExtension: fhir.Extension,
    path: (string | number)[] = [],
  ): { mapping: ExtensionTypeMapping; path: (string | number)[] } {
    // find parser for extension value
    const extensionValueParser = extensionTypeMappings.find(
      valueParser => !!_get(targetExtension, valueParser.relativePath),
    );

    if (extensionValueParser) return { mapping: extensionValueParser, path };

    // nexted extensions
    if (Array.isArray(targetExtension.extension)) {
      let value = null;
      for (let i = 0; i < targetExtension.extension.length; i++) {
        // @ts-ignore This directive was added as part of a bulk-change to enable strictNullChecks.
        value = this.getExtensionTypeMapping(targetExtension.extension[i], [
          ...path,
          'extension',
          i,
        ]);
        if (value) {
          return value;
        }
      }
    }
    throw new Error(
      `Cannot parse extension with keys: ${Object.keys(targetExtension)}`,
    );
  }

  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  getDisplayValue(fhirProperty: fhir.Extension[]) {
    const targetExtension = this.getTargetExtension(fhirProperty);

    if (!targetExtension) return null;

    // find parser for extension value
    try {
      const extensionTypeMapping =
        this.getExtensionTypeMapping(targetExtension);
      const parser = new extensionTypeMapping.mapping.Parser(this.options);
      const parseValue = _get(targetExtension, [
        ...extensionTypeMapping.path,
        extensionTypeMapping.mapping.relativePath,
      ]);
      return parser.getDisplayValue(parseValue);
    } catch (err) {
      console.error(err);
      return '';
    }
  }

  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  getSortValue(fhirProperty: fhir.Extension[]) {
    const targetExtension = this.getTargetExtension(fhirProperty);

    if (!targetExtension) return null;
    // find parser for extension value
    try {
      const extensionTypeMapping =
        this.getExtensionTypeMapping(targetExtension);
      const parser = new extensionTypeMapping.mapping.Parser(this.options);
      const parseValue = _get(targetExtension, [
        ...extensionTypeMapping.path,
        extensionTypeMapping.mapping.relativePath,
      ]);
      return parser.getSortValue(parseValue);
    } catch (err) {
      console.error(err);
      return '';
    }
  }
}

export default Extension;
